import Badge from "../common/Badge";
import Button from "../common/Button";
import BaseModal from "./BaseModal";

function displayValue(value, seen = new WeakSet()) {
  if (value === null || value === undefined) {
    return "N/A";
  }

  if (typeof value === "string") {
    const trimmedValue = value.trim();

    return trimmedValue || "N/A";
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  if (Array.isArray(value)) {
    const items = value
      .map((item) => displayValue(item, seen))
      .filter((item) => item && item !== "N/A");

    return items.length ? items.join(", ") : "N/A";
  }

  if (typeof value === "object") {
    if (seen.has(value)) {
      return "N/A";
    }

    seen.add(value);

    const preferredValue = [
      "summary",
      "text",
      "label",
      "title",
      "name",
      "value",
      "detail",
      "description",
    ]
      .map((key) => displayValue(value[key], seen))
      .find((item) => item && item !== "N/A");

    if (preferredValue) {
      return preferredValue;
    }

    const parts = Object.entries(value)
      .map(([key, entryValue]) => {
        const formattedValue = displayValue(entryValue, seen);

        if (!formattedValue || formattedValue === "N/A") {
          return "";
        }

        return `${humanizeText(key, key)}: ${formattedValue}`;
      })
      .filter(Boolean);

    return parts.length ? parts.join("; ") : "N/A";
  }

  return String(value);
}

function formatLocalDateTime(value) {
  if (value === null || value === undefined || value === "") {
    return "N/A";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return displayValue(value);
  }

  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

function humanizeText(value, fallback = "N/A") {
  const text = String(value || "").trim();

  if (!text) {
    return fallback;
  }

  return text
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function getStatusVariant(status) {
  const normalized = String(status || "").toLowerCase();

  if (normalized === "completed") {
    return "booked";
  }

  if (normalized === "failed" || normalized === "cancelled") {
    return "cancelled";
  }

  if (
    normalized === "in_progress" ||
    normalized === "initiated" ||
    normalized === "ringing"
  ) {
    return "purple";
  }

  if (normalized === "scheduled") {
    return "requested";
  }

  return "info";
}

function normalizeMetadata(metadata) {
  if (!metadata) {
    return [];
  }

  let data = metadata;

  if (typeof metadata === "string") {
    try {
      data = JSON.parse(metadata);
    } catch (error) {
      return [["value", metadata]];
    }
  }

  if (!data || typeof data !== "object" || Array.isArray(data)) {
    return [["value", displayValue(data)]];
  }

  return Object.entries(data).filter(([, value]) => {
    if (value === null || value === undefined) {
      return false;
    }

    if (typeof value === "string") {
      return Boolean(value.trim());
    }

    return true;
  });
}

function DetailItem({ label, value, isStatus = false, mono = false }) {
  const displayText = displayValue(value);

  return (
    <div className="team-profile-row">
      <span>{label}</span>
      {isStatus && displayText !== "N/A" ? (
        <div>
          <Badge variant={getStatusVariant(value)}>
            {humanizeText(value)}
          </Badge>
        </div>
      ) : (
        <strong
          className={`text-[15px] font-semibold text-gray-900 break-words ${
            mono ? "font-mono text-[13px]" : ""
          }`.trim()}
        >
          {displayText}
        </strong>
      )}
    </div>
  );
}

function DetailSection({ title, children }) {
  return (
    <div className="modal-summary-block">
      <div className="modal-summary-label">{title}</div>
      <div className="team-profile-grid">{children}</div>
    </div>
  );
}

export default function QueueDetailsModal({ open, queueCall, onClose }) {
  if (!queueCall) {
    return null;
  }

  const metadataEntries = normalizeMetadata(queueCall.metadata).filter(
    ([key]) =>
      ![
        "caller_name",
        "phone_number",
        "service_type",
        "booking_status",
        "summary_preview",
        "summary",
        "source",
        "reason",
        "call_log_id",
      ].includes(String(key || "").trim()),
  );
  const notesOrReason = queueCall.notes || queueCall.reason;
  const queueStatus = queueCall.call_status || queueCall.status;

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title="Call Queue Details"
      meta={`${displayValue(queueCall.patient_name)} • ${displayValue(
        queueCall.phone,
      )}`}
      modalId="queue-details-modal"
      className="team-modal"
    >
      <div className="space-y-5">
        <DetailSection title="Patient / Lead Info">
          <DetailItem label="Patient Name" value={queueCall.patient_name} />
          <DetailItem label="Phone Number" value={queueCall.phone} />
          <DetailItem label="Priority" value={queueCall.priority} />
          <DetailItem
            label="Source"
            value={humanizeText(queueCall.source)}
          />
          <DetailItem
            label="Service Interest"
            value={queueCall.service_type || queueCall.service_interest}
          />
          <DetailItem
            label="Booking Status"
            value={humanizeText(queueCall.booking_status)}
          />
          <DetailItem
            label="Preferred Time"
            value={formatLocalDateTime(queueCall.preferred_time)}
          />
          <DetailItem label="Notes / Reason" value={notesOrReason} />
          <DetailItem label="Offer Message" value={queueCall.offer_message} />
        </DetailSection>

        {/* <div className="modal-summary-block">
          <div className="modal-summary-label">Summary Preview</div>
          <ReadableSummary summary={summaryPreview} />
        </div> */}

        <DetailSection title="Queue / Call Info">
          {/* <DetailItem label="Call ID" value={queueCall.call_id} /> */}
          {/* <DetailItem
            label="Call UUID"
            value={queueCall.call_uuid}
            mono
          /> */}
          {/* <DetailItem
            label="Direction"
            value={humanizeText(queueCall.direction)}
          /> */}
          {/* <DetailItem
            label="Channel Type"
            value={humanizeText(queueCall.channel_type)}
          /> */}
          <DetailItem
            label="Call Status"
            value={queueStatus}
            isStatus
          />
          <DetailItem
            label="Disposition"
            value={humanizeText(queueCall.disposition)}
          />
          {/* <DetailItem
            label="Source"
            value={humanizeText(queueCall.source)}
          /> */}
          {/* <DetailItem
            label="Call Log ID"
            value={queueCall.call_log_id}
            mono
          /> */}
          <DetailItem
            label="Assigned At"
            value={formatLocalDateTime(queueCall.assigned_at)}
          />
          <DetailItem
            label="Created At"
            value={formatLocalDateTime(queueCall.created_at)}
          />
          <DetailItem
            label="Updated At"
            value={formatLocalDateTime(queueCall.updated_at)}
          />
          {/* <DetailItem label="Schedule ID" value={queueCall.schedule_id} />
          <DetailItem label="Contact ID" value={queueCall.contact_id} />
          <DetailItem label="Campaign ID" value={queueCall.campaign_id} />
          <DetailItem label="Agent ID" value={queueCall.agent_id} /> */}
        </DetailSection>

        <DetailSection title="Attempt Info">
          {/* <DetailItem label="Attempt ID" value={queueCall.attempt_id} /> */}
          {/* <DetailItem label="Attempt Number" value={queueCall.attempt_no} /> */}
          <DetailItem
            label="Attempt Status"
            value={queueCall.attempt_status}
            isStatus
          />
          <DetailItem
            label="Attempt Created At"
            value={formatLocalDateTime(queueCall.attempt_created_at)}
          />
          {/* <DetailItem
            label="Attempt Updated At"
            value={formatLocalDateTime(queueCall.attempt_updated_at)}
          /> */}
          <DetailItem
            label="Started At"
            value={formatLocalDateTime(queueCall.started_at)}
          />
          {/* <DetailItem
            label="Ended At"
            value={formatLocalDateTime(queueCall.ended_at)}
          />
          <DetailItem
            label="Failure Reason"
            value={queueCall.failure_reason}
          />
          <DetailItem
            label="Telephony Session ID"
            value={queueCall.telephony_session_id}
            mono
          />
          <DetailItem label="Recording ID" value={queueCall.recording_id} /> */}
        </DetailSection>

        {metadataEntries.length ? (
          <div className="modal-summary-block">
            {/* <div className="modal-summary-label">Metadata</div> */}
            <div className="team-profile-grid">
              {metadataEntries.map(([key, value]) => (
                <DetailItem
                  key={key}
                  label={humanizeText(key)}
                  value={displayValue(value)}
                />
              ))}
            </div>
          </div>
        ) : null}

        <div className="modal-actions">
          <Button onClick={onClose}>Close</Button>
        </div>
      </div>
    </BaseModal>
  );
}
